/**
 * @module pages/Admin
 * @description Admin dashboard page with tabbed interface for admin operations.
 * Provides access to user creation and download link regeneration tools.
 */

import React from 'react';
import cx from 'classnames';
import { FaChevronRight } from 'react-icons/fa';

import CreateUser from './CreateUser';
import RegenerateLink from './RegenerateLink';

const CREATE_USER = 0;
const REGENERATE_DOWNLOAD_LINK = 1;

/**
 * @func getInitialTab
 * @summary determines initial admin panel (tab) to show based on query params
 * @returns {string} url for initial tab, based on if url query params contains term `regenerateOrder`
 */
const getInitialTab = () => {
  const orderId = new URLSearchParams(window.location.search).get(
    'regenerateOrder',
  );
  return orderId ? REGENERATE_DOWNLOAD_LINK : CREATE_USER;
};

/**
 * @component Admin
 * @summary React Component for admin shell
 * @type {React.FC}
 * @returns {ReactNode} markup for admin shell
 */
function Admin() {
  const [activeTab, setActiveTab] = React.useState(getInitialTab);

  /**
   * @func renderButton
   * @summary renders a button to set the active tab
   * @param {string | ReactNode} label button text/label
   * @param {string} panelId admin panel id
   * @returns {ReactNode} button markup, sets active tab when clicked
   */
  const renderButton = (label, panelId) => (
    <div
      role="button"
      className={cx('h4 button', {
        admin__nav__selected: activeTab === panelId,
      })}
      onClick={() => setActiveTab(panelId)}
    >
      {label}
    </div>
  );

  /**
   * @func renderPanel
   * @summary renders panel with given component
   * @param {React.FC} Component component to render (currently `CreateUser` | `RegenerateLink`)
   * @param {string} panelId admin panel id
   * @returns {ReactNode} panel markup, will be hidden if `activeTab !== panelId`
   */
  const renderPanel = (Component, panelId) => (
    <div
      className={cx('fill z0', {
        'hidden abs': activeTab !== panelId,
        z1: activeTab === panelId,
      })}
    >
      <Component />
    </div>
  );

  return (
    <div className="ac x container--l mxa auth__none admin__container f jcc rel">
      <nav className="admin__nav z3 bcb">
        <div className="abs top right left x aic jcc admin__nav__indicator">
          <FaChevronRight className="" />
        </div>

        {renderButton('Create User', CREATE_USER)}
        {renderButton('Regenerate Download Link', REGENERATE_DOWNLOAD_LINK)}
      </nav>

      <div className="x rel admin__content">
        {renderPanel(CreateUser, CREATE_USER)}
        {renderPanel(RegenerateLink, REGENERATE_DOWNLOAD_LINK)}
      </div>
    </div>
  );
}

export default Admin;
